/** @jsxImportSource @emotion/react */
import React, { FC, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { generatePath, useNavigate, useParams } from 'react-router-dom';
import { AxiosError } from 'axios';
import { Box, CircularProgress, Fade } from '@mui/material';
import { MenuItemResponse } from 'api/models';
import { ErrorResponseCode } from 'api/models/error-response-code';
import { useCartContext } from 'components/Cart/cartContext';
import MainButton from 'components/MainButton';
import { useCheckout } from 'hooks/useCheckout';
import { useSearch } from 'hooks/useSearch';
import { useIsEditable } from 'pages/Checkout/useIsEditable';
import MenuEmpty from 'pages/Menu/components/MenuEmpty';
import MenuError from 'pages/Menu/components/MenuError';
import MenuList from 'pages/Menu/components/MenuList';
import MenuNoResults from 'pages/Menu/components/MenuNoResults';
import MenuSearch from 'pages/Menu/components/MenuSearch';
import { useGetOrderApi } from 'pages/Week/hooks/useGetOrderApi';
import { TXT_ORDER } from 'translations';
import { ROUTES } from 'utils/routes';
import { formatCurrency } from 'utils/string/formatCurrency';
import { ErrorWithCode } from 'utils/types/errorTypes';
import { useApi } from './useApi';

const Menu: FC = () => {
  const { t } = useTranslation();
  const { dayDate, userId } = useParams<{ dayDate: string; userId: string }>();
  const navigate = useNavigate();
  const { meals, addMeal, removeMeal } = useCartContext();
  const { orderResponse } = useGetOrderApi(dayDate);
  const { totalMealCount, totalPrice } = useCheckout(meals, dayDate as string);
  const { getMenu } = useApi();
  const menu = getMenu.data?.data?.items || [];
  const menuType = getMenu.data?.data?.menuType;
  const { search, setSearch, debouncedValue, filteredMeals } = useSearch(menu);

  const handleCheckout = () => {
    navigate(generatePath(ROUTES.CHECKOUT, { dayDate, userId }));
  };

  const handleAddToCart = useCallback(
    (item: MenuItemResponse) => () => {
      if (menuType) {
        addMeal(item, dayDate as string, menuType);
      }
    },
    [dayDate, menuType],
  );

  const handleRemoveFromCart = useCallback(
    (item: MenuItemResponse) => () => {
      if (menuType) {
        removeMeal(item, dayDate as string, menuType);
      }
    },
    [dayDate, menuType],
  );

  const isEditable = useIsEditable(orderResponse);

  if (getMenu.isError) {
    const error = getMenu.error as AxiosError<ErrorWithCode>;
    // A missing menu for the day is not an error — show a friendly empty state.
    const isNoMenu =
      error?.response?.status === 404 ||
      error?.response?.data?.code === ErrorResponseCode.MenuNotFound;

    return isNoMenu ? <MenuEmpty dayDate={dayDate} /> : <MenuError />;
  }

  if (getMenu.isLoading) {
    return (
      <Box display="flex" justifyContent="center">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Fade in={getMenu.isSuccess}>
      <Box>
        <MenuSearch search={search} setSearch={setSearch} />

        {menu.length && debouncedValue && filteredMeals.length === 0 ? (
          <MenuNoResults />
        ) : (
          <MenuList
            items={filteredMeals}
            cart={meals}
            onAdd={handleAddToCart}
            onRemove={handleRemoveFromCart}
            editable={isEditable}
            menuType={menuType}
            dayDate={dayDate}
          />
        )}

        <MainButton visible={!!totalMealCount} onClick={handleCheckout}>
          {t(TXT_ORDER)} ({formatCurrency(totalPrice)})
        </MainButton>
      </Box>
    </Fade>
  );
};

export default Menu;
